/**
 * Comparing OML assessments over time — the logic behind Compare over time on
 * the OML page, ported from EOS Lite's `frontend/src/lib/omlCompare.js` so the
 * two apps read the same results the same way: one line per company, results
 * grouped by company, and two results compared down to the answers that moved.
 *
 * The numbers themselves come from GET /api/assessments/compare, which works
 * the averages out by the same rule as an opened assessment.
 */

export interface CompareCriterion {
  CriterionId: number;
  DimensionId: number;
  Text: string;
  LevelDescriptors: string | null;
}

export interface CompareDimension {
  DimensionId: number;
  Name: string;
  criteria: CompareCriterion[];
}

export interface CompareTemplate {
  TemplateId: number;
  Code: string;
  Name: string;
  Version: number;
  IsActive: boolean;
  dimensions: CompareDimension[];
}

export interface CompareRun {
  AssessmentId: number;
  TemplateId: number;
  Title: string;
  Company: string | null;
  /** yyyy-mm-dd: when it was conducted, else when it was made. */
  Date: string | null;
  Status: string;
  overall: number | null;
  /** DimensionId -> average 1-5 */
  dimensions: Record<number, number>;
  /** CriterionId -> 1-5 */
  scores: Record<number, number>;
}

export interface CompareData {
  templates: CompareTemplate[];
  assessments: CompareRun[];
}

/** 'overall', or a DimensionId. */
export type Series = 'overall' | number;

export const runDate = (r: CompareRun) => r.Date ?? '';

/** Oldest first; same day, then in the order they were made. */
export const sortRuns = (runs: CompareRun[]) =>
  [...runs].sort((a, b) => runDate(a).localeCompare(runDate(b)) || a.AssessmentId - b.AssessmentId);

export const scoreOf = (r: CompareRun, series: Series): number | null =>
  series === 'overall' ? r.overall : (r.dimensions[series] ?? null);

/**
 * A company's colour slot on the trend. Four, validated against both
 * surfaces (light and dark) with the dataviz palette checker; slots 3 and 4
 * are also dashed, because crimson and light crimson sit close together when
 * two lines cross. Past four, the chart shows the first four companies — the
 * tables below still show every one.
 */
export const COMPANY_SLOTS = 4;

export interface CompanyGroup {
  key: string;
  company: string;
  /** 0-based colour slot, or null past COMPANY_SLOTS. */
  slot: number | null;
  runs: CompareRun[];
}

/**
 * Sorted runs split by company, matched ignoring case, in order of each
 * company's first result; runs with no company form one "No company" group.
 * The slot follows the company, not its rank, so it keeps its colour.
 */
export function companyGroups(runs: CompareRun[]): CompanyGroup[] {
  const byCompany = new Map<string, CompanyGroup>();
  for (const r of runs) {
    const company = (r.Company ?? '').trim();
    const key = company.toLowerCase();
    if (!byCompany.has(key)) byCompany.set(key, { key, company: company || 'No company', slot: null, runs: [] });
    byCompany.get(key)!.runs.push(r);
  }
  return [...byCompany.values()].map((g, i) => ({ ...g, slot: i < COMPANY_SLOTS ? i : null }));
}

export interface TrendPoint {
  id: number;
  slot: number;
  date: string;
  label: string;
  value: number | null;
  prev: number | null;
}

/**
 * The trend, one line per company. `dates` are the x-axis slots — every
 * distinct result date, oldest first — and each point carries its `slot` on
 * that axis.
 */
export function companyTrends(runs: CompareRun[], series: Series) {
  const dates = [...new Set(runs.map(runDate))].sort();
  const lines = companyGroups(runs)
    .filter((g) => g.slot !== null)
    .map((g) => ({
      key: g.key,
      company: g.company,
      colourSlot: g.slot as number,
      points: g.runs.map<TrendPoint>((r, i) => ({
        id: r.AssessmentId,
        slot: dates.indexOf(runDate(r)),
        date: runDate(r),
        label: r.Title,
        value: scoreOf(r, series),
        prev: i > 0 ? scoreOf(g.runs[i - 1]!, series) : null,
      })),
    }));
  return { dates, lines };
}

/**
 * The two results to compare by default: the most recent one that has an
 * earlier one for the same company, and that earlier one; else the last two.
 */
export function defaultPair(runs: CompareRun[]): { from: CompareRun | undefined; to: CompareRun | undefined } {
  const key = (r: CompareRun) => (r.Company ?? '').trim().toLowerCase();
  for (let i = runs.length - 1; i > 0; i--) {
    for (let j = i - 1; j >= 0; j--) {
      if (key(runs[j]!) === key(runs[i]!)) return { from: runs[j], to: runs[i] };
    }
  }
  return { from: runs[runs.length - 2] ?? runs[0], to: runs[runs.length - 1] };
}

/** Change from the first to the last value that has a score, or null. */
export function firstToLast(values: (number | null)[]): number | null {
  const scored = values.filter((v): v is number => v !== null);
  return scored.length > 1 ? scored[scored.length - 1]! - scored[0]! : null;
}

/** Department scores side by side for two runs, biggest gain first. */
export function departmentChanges(dimensions: CompareDimension[], from: CompareRun, to: CompareRun) {
  return dimensions
    .map((d) => {
      const a = from.dimensions[d.DimensionId] ?? null;
      const b = to.dimensions[d.DimensionId] ?? null;
      return { ...d, from: a, to: b, delta: a !== null && b !== null ? b - a : null };
    })
    .filter((d) => d.from !== null || d.to !== null)
    .sort((p, q) => (q.delta ?? -99) - (p.delta ?? -99));
}

export type ChangeKind = 'up' | 'down' | 'same' | 'added' | 'removed';

export interface AnswerChange {
  criterion: CompareCriterion;
  dimension: CompareDimension;
  before: number | null;
  after: number | null;
  kind: ChangeKind;
}

/** Every question answered in either run, and which way it moved. */
export function answerChanges(dimensions: CompareDimension[], from: CompareRun, to: CompareRun): AnswerChange[] {
  const out: AnswerChange[] = [];
  for (const dimension of dimensions) {
    for (const criterion of dimension.criteria) {
      const before = from.scores[criterion.CriterionId] ?? null;
      const after = to.scores[criterion.CriterionId] ?? null;
      if (before === null && after === null) continue;
      const kind: ChangeKind =
        before === null ? 'added' : after === null ? 'removed' : after > before ? 'up' : after < before ? 'down' : 'same';
      out.push({ criterion, dimension, before, after, kind });
    }
  }
  return out;
}

export function countKinds(changes: AnswerChange[]): Record<ChangeKind, number> {
  const c: Record<ChangeKind, number> = { up: 0, down: 0, same: 0, added: 0, removed: 0 };
  for (const x of changes) c[x.kind]++;
  return c;
}

/** The wording of the option a 1-5 score stands for, if the question has one. */
export function optionText(criterion: CompareCriterion, score: number | null): string | null {
  if (score === null || !criterion.LevelDescriptors) return null;
  try {
    return (JSON.parse(criterion.LevelDescriptors) as Record<string, string>)[String(score)] ?? null;
  } catch {
    return null;
  }
}

/** A-E for levels 1-5, the letters the EOS app shows beside an answer. */
export const letter = (score: number | null) => (score === null ? '' : 'ABCDE'[score - 1] ?? '');
